import type { Provider } from "@/lib/data/market";

export const ADDRESS_NOT_VERIFIED_LABEL = "Address not verified";

const AU_STATE_POSTCODE_PATTERN =
  /\b(?:ACT|NSW|NT|QLD|SA|TAS|VIC|WA)\s+\d{4}\b/i;
const STREET_LEVEL_PATTERN =
  /\b(?:arcade|avenue|ave|boulevard|bvd|circuit|cct|close|cl|court|ct|crescent|cres|drive|dr|floor|highway|hwy|lane|ln|level|mall|parade|pde|place|pl|plaza|road|rd|shop|square|sq|street|st|suite|terrace|tce|unit|way)\b/i;
const INTERSECTION_PATTERN = /\b(?:road|rd|street|st|avenue|ave)\b.*&/i;

function compact(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

export function getVerifiedProviderAddress(provider: Pick<Provider, "address">) {
  const address = compact(provider.address ?? "");

  if (!address || address.startsWith(",")) {
    return null;
  }

  const hasStreetNumberOrUnit = /\d/.test(address);
  const hasStreetLevelSignal =
    STREET_LEVEL_PATTERN.test(address) || INTERSECTION_PATTERN.test(address);
  const hasAustralianStatePostcode = AU_STATE_POSTCODE_PATTERN.test(address);

  if (hasStreetLevelSignal && (hasStreetNumberOrUnit || hasAustralianStatePostcode)) {
    return address;
  }

  return null;
}

export function getProviderAddressDisplay(provider: Pick<Provider, "address">) {
  return getVerifiedProviderAddress(provider) ?? ADDRESS_NOT_VERIFIED_LABEL;
}

export function buildGoogleMapsListingUrl(
  provider: Pick<Provider, "name" | "city" | "state" | "address" | "googlePlaceId">,
) {
  const placeId = compact(provider.googlePlaceId ?? "");

  if (!placeId) {
    return null;
  }

  const query = compact(
    [provider.name, getVerifiedProviderAddress(provider) ?? "", provider.city, provider.state]
      .filter(Boolean)
      .join(" "),
  );

  const params = new URLSearchParams({
    api: "1",
    query,
    query_place_id: placeId,
  });

  return `https://www.google.com/maps/search/?${params.toString()}`;
}
